import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

const TAB = 0.2;
const VIEW0 = -TAB;
const VIEW = 1 + TAB * 2;

function edge(t, x0, y0, x1, y1, nx, ny) {
  if (!t) return `L ${x1} ${y1}`;
  const dx = x1 - x0;
  const dy = y1 - y0;
  const s = t > 0 ? 1 : -1;
  const p = (u, o) => [
    x0 + dx * u + nx * o * s * TAB,
    y0 + dy * u + ny * o * s * TAB,
  ];
  const a = p(0.38, 0);
  const b = p(0.34, 0.55);
  const c = p(0.5, 1);
  const d = p(0.66, 0.55);
  const f = p(0.62, 0);
  return `L ${a[0]} ${a[1]} C ${b[0]} ${b[1]} ${b[0]} ${b[1]} ${c[0]} ${c[1]} C ${d[0]} ${d[1]} ${d[0]} ${d[1]} ${f[0]} ${f[1]} L ${x1} ${y1}`;
}

function jigsawPath(n, e, s, w) {
  return [
    'M 0 0',
    edge(n, 0, 0, 1, 0, 0, -1),
    edge(e, 1, 0, 1, 1, 1, 0),
    edge(s, 1, 1, 0, 1, 0, 1),
    edge(w, 0, 1, 0, 0, -1, 0),
    'Z',
  ].join(' ');
}

function PuzzlePiece({ piece, size, image, edges, clipKey = 'p', rot = 0, className, style, onPointerDown, onContextMenu }) {
  const col = piece % size;
  const row = Math.floor(piece / size);
  const e = edges?.[piece] || { n: 0, e: 0, s: 0, w: 0 };
  const path = jigsawPath(e.n, e.e, e.s, e.w);
  const clipId = `puzzle-clip-${clipKey}-${piece}`;
  return (
    <button
      type="button"
      onPointerDown={onPointerDown}
      onContextMenu={onContextMenu}
      className={className}
      style={{ overflow: 'visible', ...style }}
    >
      <span
        className="block w-full h-full pointer-events-none"
        style={rot ? { transform: `rotate(${rot}deg)` } : undefined}
      >
        <svg viewBox={`${VIEW0} ${VIEW0} ${VIEW} ${VIEW}`} className="w-full h-full block overflow-visible">
          <defs>
            <clipPath id={clipId} clipPathUnits="userSpaceOnUse">
              <path d={path} />
            </clipPath>
          </defs>
          <g clipPath={`url(#${clipId})`}>
            {image ? (
              <image
                href={image}
                x={-col}
                y={-row}
                width={size}
                height={size}
                preserveAspectRatio="none"
              />
            ) : (
              <rect x="0" y="0" width="1" height="1" fill="#f76f4b" />
            )}
          </g>
          <path d={path} fill="none" stroke="rgba(0,0,0,0.45)" strokeWidth="0.025" />
        </svg>
      </span>
    </button>
  );
}

function makeEdges(size, seed) {
  let x = 2166136261;
  const s = String(seed || 'p');
  for (let i = 0; i < s.length; i += 1) x = Math.imul(x ^ s.charCodeAt(i), 16777619) >>> 0;
  const next = () => {
    x = Math.imul(x ^ (x >>> 16), 2246822507) >>> 0;
    return x;
  };
  const choice = () => (next() % 2 === 0 ? 1 : -1);
  const horizontal = Array.from({ length: size }, () => Array.from({ length: size - 1 }, choice));
  const vertical = Array.from({ length: size - 1 }, () => Array.from({ length: size }, choice));
  const edges = [];
  for (let row = 0; row < size; row += 1) {
    for (let col = 0; col < size; col += 1) {
      edges.push({
        n: row === 0 ? 0 : -vertical[row - 1][col],
        e: col === size - 1 ? 0 : horizontal[row][col],
        s: row === size - 1 ? 0 : vertical[row][col],
        w: col === 0 ? 0 : -horizontal[row][col - 1],
      });
    }
  }
  return edges;
}

function snapRot(rot) {
  return (((Math.round((Number(rot) || 0) / 90) * 90) % 360) + 360) % 360;
}

function clampTrayPos(left, top, rot, z, trayW, trayH, piecePx) {
  const maxL = Math.max(4, trayW - piecePx);
  const maxT = Math.max(22, trayH - piecePx);
  return {
    left: Math.min(maxL, Math.max(4, Number(left) || 0)),
    top: Math.min(maxT, Math.max(22, Number(top) || 0)),
    rot: snapRot(rot),
    z: z || 1,
  };
}

function isMostlyVisible(pos, trayW, trayH, piecePx) {
  if (!pos || !Number.isFinite(pos.left) || !Number.isFinite(pos.top)) return false;
  const visW = Math.min(trayW, pos.left + piecePx) - Math.max(0, pos.left);
  const visH = Math.min(trayH, pos.top + piecePx) - Math.max(0, pos.top);
  return visW > piecePx * 0.55 && visH > piecePx * 0.55;
}

function jumblePos(id, index, count, seed, trayW, trayH, piecePx) {
  let x = 2166136261;
  const s = `${seed || 'p'}#${id}`;
  for (let i = 0; i < s.length; i += 1) x = Math.imul(x ^ s.charCodeAt(i), 16777619) >>> 0;
  const next = () => {
    x = Math.imul(x ^ (x >>> 16), 2246822507) >>> 0;
    return x;
  };
  const u = () => (next() % 10000) / 10000;
  const cols = Math.max(1, Math.ceil(Math.sqrt(Math.max(1, count))));
  const rows = Math.max(1, Math.ceil(Math.max(1, count) / cols));
  const col = index % cols;
  const row = Math.floor(index / cols);
  const cellW = trayW / cols;
  const cellH = (trayH - 20) / rows;
  const jitterX = (u() - 0.5) * Math.min(16, cellW * 0.15);
  const jitterY = (u() - 0.5) * Math.min(16, cellH * 0.15);
  const left = col * cellW + Math.max(0, (cellW - piecePx) / 2) + jitterX;
  const top = 20 + row * cellH + Math.max(0, (cellH - piecePx) / 2) + jitterY;
  return clampTrayPos(left, top, (next() % 4) * 90, 1 + (next() % 40), trayW, trayH, piecePx);
}

function initialSlots(count, savedSlots) {
  if (savedSlots?.length === count) return [...savedSlots];
  return Array.from({ length: count }, () => null);
}

function asPieceId(v) {
  const n = Number(v);
  return Number.isInteger(n) ? n : v;
}

function reconcileTray(pieces, slots, tray) {
  const used = new Set((slots || []).filter((v) => v != null).map(asPieceId));
  const remaining = (pieces || []).map(asPieceId).filter((id) => !used.has(id));
  const remainingSet = new Set(remaining);
  const seen = new Set();
  const ordered = [];
  (tray || []).map(asPieceId).concat(remaining).forEach((id) => {
    if (!remainingSet.has(id) || seen.has(id)) return;
    seen.add(id);
    ordered.push(id);
  });
  return ordered;
}

function initialTrayPos(ids, seed, trayW, trayH, piecePx) {
  const pos = {};
  ids.forEach((id, index) => {
    pos[String(id)] = jumblePos(id, index, ids.length, seed, trayW, trayH, piecePx);
  });
  return pos;
}

function scaleTrayPos(pos, fromW, fromH, toW, toH, piecePx) {
  const sx = toW / Math.max(1, fromW);
  const sy = toH / Math.max(1, fromH);
  return clampTrayPos(
    (Number(pos?.left) || 0) * sx,
    (Number(pos?.top) || 0) * sy,
    pos?.rot,
    pos?.z,
    toW,
    toH,
    piecePx,
  );
}

export default function PuzzleBoard({ payload, savedSlots, savedTray, onSolved, onProgress }) {
  const size = payload?.size || 3;
  const image = payload?.image;
  const seed = payload?.seed || '';
  const count = size * size;
  const edges = payload?.edges?.length === count ? payload.edges : makeEdges(size, seed);
  const pieces = payload?.pieces?.length === count
    ? payload.pieces
    : Array.from({ length: count }, (_, i) => i);
  const wrapRef = useRef(null);
  const [panePx, setPanePx] = useState(0);
  const [previewOpen, setPreviewOpen] = useState(false);
  const boardPx = panePx || 360;
  const cellPx = boardPx / size;
  const placedPx = cellPx * VIEW;
  const placedOff = cellPx * TAB;
  const trayW = boardPx;
  const trayH = boardPx;
  const [slots, setSlots] = useState(() => initialSlots(count, savedSlots));
  const [tray, setTray] = useState(() => reconcileTray(pieces, initialSlots(count, savedSlots), savedTray));
  const [trayPos, setTrayPos] = useState(() => initialTrayPos(
    reconcileTray(pieces, initialSlots(count, savedSlots), savedTray),
    seed,
    trayW,
    trayH,
    placedPx,
  ));
  const [drag, setDrag] = useState(null);
  const slotsRef = useRef(slots);
  const trayRef = useRef(tray);
  const trayPosRef = useRef(trayPos);
  const trayElRef = useRef(null);
  const dragRef = useRef(null);
  const solvedRef = useRef(false);
  const zRef = useRef(40);
  const paneRef = useRef(0);
  const metricsRef = useRef({ boardPx, cellPx, placedPx, placedOff, trayW, trayH });
  dragRef.current = drag;
  metricsRef.current = { boardPx, cellPx, placedPx, placedOff, trayW, trayH };

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return undefined;
    const update = () => {
      const w = el.clientWidth;
      const stacked = typeof window !== 'undefined' && window.matchMedia('(max-width: 639px)').matches;
      const gap = 16;
      const dialog = el.closest('[data-game-season-dialog]');
      const scroller = dialog?.querySelector('.overflow-y-auto') || el.closest('.overflow-y-auto');
      const bottom = scroller ? scroller.getBoundingClientRect().bottom : window.innerHeight - 16;
      const top = el.getBoundingClientRect().top;
      const availH = Math.max(220, bottom - top - 8);
      const fromW = stacked ? w : (w - gap) / 2;
      setPanePx(Math.max(220, Math.floor(Math.min(fromW, availH))));
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    window.addEventListener('resize', update);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', update);
    };
  }, [payload]);

  useEffect(() => {
    const prev = paneRef.current;
    paneRef.current = panePx;
    if (!panePx) return;
    const piecePx = (panePx / size) * VIEW;
    if (!prev) {
      const nextPos = initialTrayPos(trayRef.current, seed, panePx, panePx, piecePx);
      trayPosRef.current = nextPos;
      setTrayPos(nextPos);
      return;
    }
    if (prev === panePx) return;
    const nextPos = {};
    Object.entries(trayPosRef.current).forEach(([key, pos]) => {
      nextPos[key] = scaleTrayPos(pos, prev, prev, panePx, panePx, piecePx);
    });
    trayPosRef.current = nextPos;
    setTrayPos(nextPos);
  }, [panePx, size]);

  useEffect(() => {
    const { trayW: tw, trayH: th, placedPx: px } = metricsRef.current;
    const nextSlots = initialSlots(count, savedSlots);
    const nextTray = reconcileTray(pieces, nextSlots, savedTray);
    const nextPos = initialTrayPos(nextTray, seed, tw || 360, th || 360, px || 80);
    slotsRef.current = nextSlots;
    trayRef.current = nextTray;
    trayPosRef.current = nextPos;
    setSlots(nextSlots);
    setTray(nextTray);
    setTrayPos(nextPos);
    setDrag(null);
    solvedRef.current = false;
    if (nextSlots.length === count && nextSlots.every((v, i) => v === i) && !solvedRef.current) {
      solvedRef.current = true;
      onSolved?.();
    }
  }, [payload]);

  const persist = (nextSlots, nextTray, nextPos) => {
    const { trayW: tw, trayH: th, placedPx: px } = metricsRef.current;
    const trayNext = reconcileTray(pieces, nextSlots, nextTray);
    const posNext = { ...nextPos };
    trayNext.forEach((id, index) => {
      const key = String(id);
      if (!isMostlyVisible(posNext[key], tw, th, px)) {
        posNext[key] = jumblePos(id, index, trayNext.length, seed, tw, th, px);
      }
    });
    Object.keys(posNext).forEach((key) => {
      if (!trayNext.some((id) => String(id) === key)) delete posNext[key];
    });
    slotsRef.current = nextSlots;
    trayRef.current = trayNext;
    trayPosRef.current = posNext;
    setSlots(nextSlots);
    setTray(trayNext);
    setTrayPos(posNext);
    onProgress?.({ slots: nextSlots, tray: trayNext, trayPos: posNext });
    if (nextSlots.every((v, i) => v === i) && !solvedRef.current) {
      solvedRef.current = true;
      onSolved?.();
    }
  };

  const startDrag = (e, piece, from, slot) => {
    if (solvedRef.current || piece == null || from === 'slot') return;
    if (e.button != null && e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation();
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* ignore */ }
    const { placedPx: px } = metricsRef.current;
    const rect = e.currentTarget.getBoundingClientRect();
    const scaleX = rect.width ? px / rect.width : 1;
    const scaleY = rect.height ? px / rect.height : 1;
    const ox = (e.clientX - rect.left) * scaleX;
    const oy = (e.clientY - rect.top) * scaleY;
    const rot = from === 'tray' ? snapRot(trayPosRef.current[String(piece)]?.rot) : 0;
    zRef.current += 1;
    setDrag({
      piece,
      from,
      slot,
      ox,
      oy,
      rot,
      x: e.clientX - ox,
      y: e.clientY - oy,
      w: px,
      h: px,
    });
  };

  const rotatePiece = (e, piece) => {
    e.preventDefault();
    e.stopPropagation();
    if (solvedRef.current || piece == null || drag) return;
    const { trayW: tw, trayH: th, placedPx: px } = metricsRef.current;
    const key = String(piece);
    const nextPos = { ...trayPosRef.current };
    const current = nextPos[key] || jumblePos(piece, trayRef.current.indexOf(piece), trayRef.current.length, seed, tw, th, px);
    nextPos[key] = { ...current, rot: (snapRot(current.rot) + 90) % 360 };
    persist(slotsRef.current, trayRef.current, nextPos);
  };

  useEffect(() => {
    if (!previewOpen) return undefined;
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      e.stopPropagation();
      setPreviewOpen(false);
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [previewOpen]);

  useEffect(() => {
    if (!drag) return undefined;
    const moveDrag = (e) => {
      setDrag((d) => {
        if (!d) return d;
        const next = { ...d, x: e.clientX - d.ox, y: e.clientY - d.oy };
        dragRef.current = next;
        return next;
      });
    };
    const rotateHeld = (e) => {
      e.preventDefault();
      e.stopPropagation();
      setDrag((d) => {
        if (!d) return d;
        const next = { ...d, rot: (snapRot(d.rot) + 90) % 360 };
        dragRef.current = next;
        return next;
      });
    };
    let finished = false;
    const endDrag = (e) => {
      if (e.button != null && e.button !== 0) return;
      if (finished) return;
      finished = true;
      const current = dragRef.current;
      setDrag(null);
      dragRef.current = null;
      if (!current) return;
      const under = document.elementFromPoint(e.clientX, e.clientY);
      const slotEl = under?.closest?.('[data-puzzle-slot]');
      const dest = slotEl ? Number(slotEl.getAttribute('data-puzzle-slot')) : NaN;
      const nextSlots = [...slotsRef.current];
      let nextTray = [...trayRef.current];
      const nextPos = { ...trayPosRef.current };
      if (dest === current.piece && nextSlots[dest] == null && snapRot(current.rot) === 0) {
        if (current.from === 'tray') nextTray = nextTray.filter((id) => id !== current.piece);
        if (current.from === 'slot' && current.slot != null) nextSlots[current.slot] = null;
        nextSlots[dest] = current.piece;
        delete nextPos[String(current.piece)];
        persist(nextSlots, nextTray, nextPos);
        return;
      }
      if (current.from === 'slot') {
        persist(slotsRef.current, trayRef.current, trayPosRef.current);
        return;
      }
      const trayRect = trayElRef.current?.getBoundingClientRect();
      const inTray = !!(trayRect
        && e.clientX >= trayRect.left
        && e.clientX <= trayRect.right
        && e.clientY >= trayRect.top
        && e.clientY <= trayRect.bottom);
      const key = String(current.piece);
      const prev = trayPosRef.current[key];
      if (inTray && trayRect) {
        nextPos[key] = clampTrayPos(
          e.clientX - current.ox - trayRect.left,
          e.clientY - current.oy - trayRect.top,
          current.rot,
          zRef.current,
          trayRect.width,
          trayRect.height,
          metricsRef.current.placedPx,
        );
      } else if (prev) {
        nextPos[key] = { ...prev, rot: snapRot(current.rot), z: zRef.current };
      } else {
        const { trayW: tw, trayH: th, placedPx: px } = metricsRef.current;
        nextPos[key] = jumblePos(
          current.piece,
          Math.max(0, nextTray.indexOf(current.piece)),
          nextTray.length,
          seed,
          tw,
          th,
          px,
        );
      }
      persist(nextSlots, nextTray, nextPos);
    };
    window.addEventListener('pointermove', moveDrag);
    window.addEventListener('mousemove', moveDrag);
    window.addEventListener('pointerup', endDrag, true);
    window.addEventListener('mouseup', endDrag, true);
    window.addEventListener('pointercancel', endDrag, true);
    window.addEventListener('contextmenu', rotateHeld, true);
    return () => {
      window.removeEventListener('pointermove', moveDrag);
      window.removeEventListener('mousemove', moveDrag);
      window.removeEventListener('pointerup', endDrag, true);
      window.removeEventListener('mouseup', endDrag, true);
      window.removeEventListener('pointercancel', endDrag, true);
      window.removeEventListener('contextmenu', rotateHeld, true);
    };
  }, [Boolean(drag)]);

  return (
    <div className="select-none w-full min-w-0">
      {image ? (
        <div className="mb-2 flex justify-center" style={{ width: boardPx }}>
          <button
            type="button"
            onClick={() => setPreviewOpen(true)}
            title="Agrandir le modèle"
            className="shrink-0 rounded-md border border-gray-200 dark:border-gray-700 overflow-hidden cursor-zoom-in hover:ring-2 hover:ring-[#f76f4b]/70"
          >
            <img src={image} alt="Modèle" className="w-20 h-20 object-cover block" />
          </button>
        </div>
      ) : null}
      <div ref={wrapRef} className="w-full min-w-0 flex flex-col sm:flex-row gap-4 items-stretch">
        <div className="relative shrink-0" style={{ width: boardPx, height: boardPx }}>
          <div
            className="grid"
            style={{
              gridTemplateColumns: `repeat(${size}, minmax(0, 1fr))`,
              width: boardPx,
              height: boardPx,
            }}
          >
            {slots.map((piece, i) => (
                <div
                  key={`slot-${i}`}
                  data-puzzle-slot={i}
                  className="relative aspect-square bg-gray-800/70"
                >
                  {piece != null && image ? (
                    <PuzzlePiece
                      piece={piece}
                      size={size}
                      image={image}
                      edges={edges}
                      clipKey="slot"
                      onContextMenu={(e) => { e.preventDefault(); e.stopPropagation(); }}
                      className="absolute pointer-events-none z-10"
                      style={{
                        left: -placedOff,
                        top: -placedOff,
                        width: placedPx,
                        height: placedPx,
                      }}
                    />
                  ) : null}
                </div>
            ))}
          </div>
        </div>
        <div
          ref={trayElRef}
          className="relative shrink-0 rounded-md border border-dashed border-gray-300 dark:border-gray-600 bg-gray-50/40 dark:bg-gray-900/40"
          style={{ width: boardPx, height: boardPx }}
        >
          <p className="absolute left-2 top-1 z-50 text-[10px] uppercase tracking-wide text-gray-400 pointer-events-none">Pièces</p>
          {tray.map((piece) => {
            const pos = trayPos[String(piece)] || jumblePos(
              piece,
              tray.indexOf(piece),
              tray.length,
              seed,
              trayW,
              trayH,
              placedPx,
            );
            const hiding = drag?.from === 'tray' && drag.piece === piece;
            return (
              <PuzzlePiece
                key={`tray-${piece}`}
                piece={piece}
                size={size}
                image={image}
                edges={edges}
                clipKey="tray"
                rot={pos.rot}
                onPointerDown={(e) => startDrag(e, piece, 'tray')}
                onContextMenu={(e) => rotatePiece(e, piece)}
                className="absolute cursor-grab active:cursor-grabbing touch-none drop-shadow-md overflow-visible"
                style={{
                  width: placedPx,
                  height: placedPx,
                  left: pos.left,
                  top: pos.top,
                  zIndex: pos.z,
                  opacity: hiding ? 0 : 1,
                }}
              />
            );
          })}
        </div>
      </div>
      {drag && image && typeof document !== 'undefined'
        ? createPortal(
          <div
            className="fixed pointer-events-none z-[20000] drop-shadow-xl"
            style={{
              left: drag.x,
              top: drag.y,
              width: drag.w,
              height: drag.h,
            }}
          >
            <PuzzlePiece piece={drag.piece} size={size} image={image} edges={edges} clipKey="drag" rot={drag.rot} className="w-full h-full" />
          </div>,
          document.body,
        )
        : null}
      {previewOpen && image ? (
        <div
          className="fixed inset-0 z-[30000] bg-black/80 flex items-center justify-center p-6"
          onClick={() => setPreviewOpen(false)}
          role="dialog"
          aria-label="Modèle du puzzle"
        >
          <button
            type="button"
            className="absolute top-4 right-4 text-white text-2xl leading-none opacity-80 hover:opacity-100"
            onClick={() => setPreviewOpen(false)}
            aria-label="Fermer"
          >
            ×
          </button>
          <img
            src={image}
            alt="Modèle agrandi"
            className="max-w-[min(92vw,1100px)] max-h-[90vh] rounded-lg object-contain shadow-2xl cursor-zoom-out"
            onClick={() => setPreviewOpen(false)}
          />
        </div>
      ) : null}
    </div>
  );
}
